import React, { useEffect } from 'react';
import { BrandMark } from './founding/BrandMark';
import { FoundingFooter } from './founding/FoundingFooter';
import './LandingPageV2.css';
import './founding/FoundingMemberPage.css';

const SECTIONS = [
  { title: 'Who we are', body: [
    'SchedulesOfWork.com ("SchedulesOfWork", "we", "us") turns floor plans into measured, priced schedules of work for residential refurbishment. We are the data controller for the personal data described in this notice.',
    'Questions about this notice or your data can be sent to team@schedulesofwork.com.',
  ] },
  { title: 'What we collect', list: [
    ['Founding member sign-ups', 'your email address, and any answers you choose to give: whether you are a homeowner, investor, builder or architect, what you are planning, when you are starting, your rough budget and what a priced schedule is worth to you. We also record the date you joined and the campaign link you arrived from (for example utm_source and utm_campaign).'],
    ['Accounts', 'your name, email address and a securely hashed password.'],
    ['Projects', 'floor plans and drawings you upload, room measurements, project details, scope choices and messages you send through the estimator.'],
    ['Payments', 'when payments are open, card payments are taken by Stripe. We receive confirmation of payment and the amount, but we never see or store your full card details.'],
    ['Technical data', 'essential sign-in cookies, IP address and basic request logs used to keep the service secure and working.'],
  ] },
  { title: 'How and why we use it', list: [
    ['Consent', 'to email you as a founding member about SchedulesOfWork, including your launch discount. You can withdraw consent at any time.'],
    ['Contract', 'to provide the estimator, documents and services you sign up or pay for.'],
    ['Legitimate interests', 'to understand demand and improve the service (using survey answers and campaign sources), to prevent misuse and to keep the platform secure.'],
    ['Legal obligation', 'to keep payment and accounting records as required by HMRC.'],
  ] },
  { title: 'Who we share it with', body: [
    'We do not sell your personal data. We share it only with service providers who process it on our behalf, under contracts that require them to keep it secure:',
  ], list: [
    ['Stripe', 'payment processing.'],
    ['Resend and Google (Gmail / Google Workspace)', 'sending and receiving email.'],
    ['AI processing providers', 'analysing uploaded floor plans and generating draft schedules. Uploaded data is used only to provide the service to you.'],
    ['Cloud hosting provider', 'hosting our website, database and file storage.'],
  ], after: [
    'Some of these providers process data outside the UK, including in the United States. Where they do, transfers are protected by UK adequacy regulations or the UK International Data Transfer Agreement / Addendum.',
    'We may also disclose data where required by law.',
  ] },
  { title: 'How long we keep it', list: [
    ['Founding member data', 'until you unsubscribe or ask us to delete it, and in any case no longer than 2 years from the date you joined.'],
    ['Free accounts', 'project data and documents are kept for 180 days from creation, with a reminder 30 days before expiry.'],
    ['Paid accounts', 'kept for the length of your subscription plus 90 days, and documents for at least 12 months from creation.'],
    ['Payment and accounting records', 'kept for 6 years to meet tax requirements.'],
    ['Marketing consent records', 'kept for 2 years from the date of consent.'],
  ] },
  { title: 'Cookies', body: [
    'We use only essential cookies needed to sign you in and keep your session secure. We do not use advertising or third-party tracking cookies.',
  ] },
  { title: 'Your rights', body: [
    'Under UK GDPR you have the right to access your data, correct it, have it deleted, restrict or object to its use, receive a copy in a portable format and withdraw consent at any time.',
    'To unsubscribe from founding member emails, use the link in any email or the Unsubscribe link at the bottom of this page. For any other request, email team@schedulesofwork.com. We will respond within one month.',
    "If you are unhappy with how we handle your data, you can complain to the Information Commissioner's Office (ICO) at ico.org.uk or on 0303 123 1113.",
  ] },
  { title: 'Changes to this notice', body: [
    'We may update this notice from time to time. The latest version will always be on this page.',
  ] },
];

const Para = ({ children }) => <p className="fm-privacy-p">{children}</p>;

export default function PrivacyPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'SchedulesOfWork | Privacy';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="landing-v2 fm-page fm-privacy-page" data-testid="privacy-page">
      <header className="lv2-nav"><div className="lv2-nav-inner"><BrandMark /></div></header>
      <main className="fm-privacy-main">
        <h1 className="fm-privacy-h1" data-testid="privacy-title">Privacy notice</h1>
        <p className="fm-privacy-lead">How SchedulesOfWork collects, uses and protects your personal data.</p>
        {SECTIONS.map((s, i) => (
          <section key={s.title} className="fm-privacy-sec" data-testid={`privacy-section-${i + 1}`}>
            <h2 className="fm-privacy-h2">{i + 1}. {s.title}</h2>
            {(s.body || []).map((t) => <Para key={t}>{t}</Para>)}
            {s.list && (
              <ul className="fm-privacy-list">
                {s.list.map(([k, v]) => <li key={k}><strong>{k}:</strong> {v}</li>)}
              </ul>
            )}
            {(s.after || []).map((t) => <Para key={t}>{t}</Para>)}
          </section>
        ))}
        <p className="fm-privacy-updated" data-testid="privacy-last-updated">Last updated: October 2026</p>
      </main>
      <FoundingFooter />
    </div>
  );
}
